import { useMemo } from 'react';
import { Pagination } from '@/components/ui/Pagination';
import { Table, type Column } from '@/components/ui/Table';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { formatDuration, formatNumber } from '@/lib/format';
import type { InstituteReport } from '@/types/instituteReport';
import { dayLabels } from '../utils';

type DayRow = InstituteReport['usage']['byDay'][number];

/** Rows per page — a week and a half, so a 90-day report is nine pages, not one scroll. */
const PAGE_SIZE = 10;

/** The table's page, as it sits in the address bar. */
type Filters = {
  page: number;
};

const DEFAULT_FILTERS: Filters = { page: 1 };

/** Prefixed, so a bare `page` on the report reads as nobody's — this is one panel of many. */
const FILTER_OPTIONS = { prefix: 'days' };

const COLUMNS: Column<DayRow>[] = [
  {
    key: 'day',
    header: 'Date',
    render: (row) => <span className="font-[560] text-ink">{dayLabels(row.day).label}</span>,
  },
  {
    key: 'active',
    header: 'Active users',
    align: 'right',
    render: (row) => <span className="tabular-nums">{formatNumber(row.activeUsers)}</span>,
  },
  {
    key: 'logins',
    header: 'Sign-ins',
    align: 'right',
    render: (row) => <span className="tabular-nums">{formatNumber(row.logins)}</span>,
  },
  {
    key: 'time',
    header: 'Time in the app',
    align: 'right',
    render: (row) => (
      <span className="tabular-nums">{formatDuration(Math.round(row.minutes)) ?? '—'}</span>
    ),
  },
];

/**
 * The period one day at a time, newest first — what the charts draw, as figures
 * someone can read off for a given date. Paged here rather than by the API: the
 * whole period is already in the report and is at most ninety rows.
 */
export function ActivityByDateTable({ days }: { days: DayRow[] }) {
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const newestFirst = useMemo(() => [...days].reverse(), [days]);
  const totalPages = Math.max(1, Math.ceil(newestFirst.length / PAGE_SIZE));
  // A shorter period chosen while on a late page would otherwise show nothing.
  const current = Math.min(filters.page, totalPages);
  const rows = newestFirst.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div className="flex flex-col gap-3">
      <Table
        variant="plain"
        columns={COLUMNS}
        rows={rows}
        rowKey={(row) => row.day}
        empty={<p className="p-4 text-sm text-ink-3">No days in this period.</p>}
      />
      {totalPages > 1 && (
        <Pagination
          meta={{ page: current, limit: PAGE_SIZE, total: newestFirst.length, totalPages }}
          onPageChange={(page) => setFilters({ page })}
          itemLabel="days"
          hideBorder
        />
      )}
    </div>
  );
}
